/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import {generateUUID} from '@dvrd/dvr-controls';
import {ISO_DATE_FORMAT} from '../util/constants';
import {ModelResponse} from '../util/interfaces';
import {deepClone} from '../util/utils';
import {BaseFields, BaseModel, BaseProps, FieldDefinition, ModelFieldType} from './baseModel';
import IDate, { DateType } from '@dvrd/idate';
import {getCalendarItems} from "../util/requestSender";
import {ResponseData} from '@dvrd/fetch';

interface Props extends BaseProps {
    en_label?: string;
    nl_label?: string;
    from_date?: string;
    to_date?: string | null;
    comment?: string | null;
    active?: boolean;
}

export interface AgendaItemFields extends BaseFields {
    en_label: string;
    nl_label: string;
    from_date: string;
    to_date: string | null;
    comment: string | null;
    active: boolean;
}

const fields: AgendaItemFields = {
    id: '',
    created_at: '',
    updated_at: '',
    en_label: '',
    nl_label: '',
    from_date: '',
    to_date: null,
    comment: null,
    active: true,
};

type FieldDefType = keyof AgendaItemFields;

const fieldsDefinition: Record<FieldDefType, FieldDefinition> = {
    id: new FieldDefinition(ModelFieldType.STRING),
    created_at: new FieldDefinition(ModelFieldType.STRING),
    updated_at: new FieldDefinition(ModelFieldType.STRING, {allowNull: true}),
    en_label: new FieldDefinition(ModelFieldType.STRING),
    nl_label: new FieldDefinition(ModelFieldType.STRING),
    from_date: new FieldDefinition(ModelFieldType.STRING),
    to_date: new FieldDefinition(ModelFieldType.STRING, {allowNull: true, allowUndefined: true, defaultValue: null}),
    comment: new FieldDefinition(ModelFieldType.STRING, {allowNull: true, allowUndefined: true, defaultValue: null}),
    active: new FieldDefinition(ModelFieldType.BOOLEAN),
};

const ignoreProps: Array<keyof AgendaItemFields> = [];

export default class CalendarItem extends BaseModel<Props, AgendaItemFields, FieldDefType> {
    static getAll = (fromDate: IDate, toDate: IDate, callback: ModelResponse<Array<CalendarItem>>) => {
        return getCalendarItems(
            {
                data: {from_date: fromDate.format(ISO_DATE_FORMAT), to_date: toDate.format(ISO_DATE_FORMAT)},
                callback: (data: ResponseData) => {
                    const items: Array<CalendarItem> = data.success ?
                        data.calendar_items.map((item: any) => new CalendarItem(item)) : [];
                    callback(items, data.success, data);
                }
            });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
        this.identity = props?.identity ?? `CalendarItem.${props?.id ?? generateUUID()}`;
    }

    get ENLabel(): string {
        return this.fields.en_label;
    }

    set ENLabel(value: string) {
        this.fields.en_label = value;
    }

    get NLLabel(): string {
        return this.fields.nl_label;
    }

    set NLLabel(value: string) {
        this.fields.nl_label = value;
    }

    get fromDateString(): string {
        return this.fields.from_date;
    }

    set fromDateString(date: string) {
        this.fields.from_date = date;
    }

    get fromDate(): IDate {
        return new IDate(this.fields.from_date, DateType.DATE_ONLY);
    }

    set fromDate(date: IDate) {
        this.fields.from_date = date.format(ISO_DATE_FORMAT);
    }

    get toDateString(): string | null {
        return this.fields.to_date;
    }

    set toDateString(date: string | null) {
        this.fields.to_date = date;
    }

    get toDate(): IDate | null {
        if (!this.fields.to_date) return null;
        return new IDate(this.fields.to_date, DateType.DATE_ONLY);
    }

    set toDate(date: IDate | null) {
        this.fields.to_date = date?.format(ISO_DATE_FORMAT) ?? null;
    }

    get period(): string {
        const format = 'D MMMM';
        const toDate = this.toDate;
        const fromDate = this.fromDate;
        const fromString = fromDate.format(format);
        const toString = toDate?.format(format);
        if (toString && toDate) {
            const numDays = toDate.absDiff(fromDate, 'days');
            const sameMonth = fromDate.month() === toDate.month();
            if (numDays === 0) return fromString;
            else if (numDays === 1) {
                if (sameMonth)
                    return `${fromDate.day()} en ${toString}`;
                return `${fromString} en ${toString}`;
            }
            if (sameMonth)
                return `${fromDate.day()} t/m ${toString}`;
            return `${fromString} t/m ${toString}`;
        }
        return fromString;
    }

    get extendedPeriod(): string {
        const format = 'dddd D MMMM';
        const toDate = this.toDate;
        const fromDate = this.fromDate;
        let fromString = fromDate.format(format);
        let toString = toDate?.format(format + ' YYYY');
        if (toString && toDate) {
            const sameYear = fromDate.year() === toDate.year();
            if (!sameYear) fromString += ` ${fromDate.year()}`;
            const numDays = toDate.absDiff(fromDate, 'days');
            if (numDays === 0) return fromString;
            else if (numDays === 1) {
                return `${fromString} en ${toString}`;
            }
            return `${fromString} t/m ${toString}`;
        }
        return fromString + ` ${fromDate.year()}`;
    }

    get comment(): string | null {
        return this.fields.comment;
    }

    set comment(value: string | null) {
        this.fields.comment = value;
    }

    get active(): boolean {
        return this.fields.active;
    }

    set active(value: boolean) {
        this.fields.active = value;
    }

    clone = (identityMap?: Map<string, any>): CalendarItem => {
        const props = deepClone(this.fields, identityMap);
        return new CalendarItem({...props, identity: this.identity});
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
    };
}